// @ts-strict-ignore
import { useState } from 'react';
import { withAuthorizationHeaders } from '../../api';
import { RunwayResponse } from '../../types';
import { useNonExpiredAuthCredentials } from './useNonExpiredAuthCredentials';

export type UploadFormWithFileHookData<Request, Result> = [
    Result | undefined,
    boolean,
    Error,
    (params: UploadFileParams<Request>) => Promise<void>,
];

export interface UploadFileParams<T> {
    file: File;
    formValues: T;
    uploadUrl: string;
}

// write as generic function
export function useUploadFormWithFile<
    Request extends Record<string, string>,
    Result = boolean,
>(): UploadFormWithFileHookData<Request, Result> {
    const [result, setResult] = useState<Result | undefined>(undefined);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const getNonExpiredAuthCredentials = useNonExpiredAuthCredentials();

    const uploadFile = async (params: UploadFileParams<Request>): Promise<Result> => {
        const authCredentials = await getNonExpiredAuthCredentials();

        return new Promise((resolve, reject) => {
            const uploadUrl = params.uploadUrl;
            const { file, formValues } = params;
            // create a new FormData object
            const formData = new FormData();

            // add the file to the FormData object
            formData.append('file', file);

            // add the form values to the FormData object
            for (const key in formValues) {
                formData.append(key, formValues[key]);
            }

            const xhr = new XMLHttpRequest();
            xhr.open('POST', uploadUrl, true);
            xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

            // create headers
            const headers = withAuthorizationHeaders(authCredentials);

            // add headers
            for (const key in headers) {
                xhr.setRequestHeader(key, headers[key]);
            }

            xhr.onload = () => {
                const response = JSON.parse(xhr.response) as RunwayResponse<Result>;
                if (response?.success) {
                    const result = response.data ?? (true as unknown as Result);
                    resolve(result);
                } else {
                    if (response?.errorMessage) {
                        reject(new Error(response.errorMessage));
                    } else {
                        reject(new Error(xhr.statusText));
                    }
                }
            };

            xhr.onerror = (e) => {
                reject(e);
            };

            xhr.send(formData);
        });
    };

    const startUploading = async (params: UploadFileParams<Request>) => {
        setIsLoading(true);
        setError(undefined);
        try {
            const result = await uploadFile(params);
            setResult(result);
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return [result, isLoading, error, startUploading];
}
